import { BottomSheet } from '@/components/BottomSheet/BottomSheet';
import {
  buildMaxShareLink,
  buildTelegramShareLink,
  buildWhatsAppShareLink,
} from '@/lib/messengerLinks';

/**
 * Shared «Переслать клиенту» (B-222): единое вертикальное меню (P-019) пересылки
 * товара в WhatsApp/Telegram/MAX + «Скопировать сообщение». Дедуп приватных
 * ForwardSheet (карточки декора — Блок 2, фурнитуры — Блок 5, ручек — Блок 4) +
 * реферальный экран (Блок 11). Текст-агностичен: call-site отдаёт РАЗДЕЛЬНО
 * `message` (сообщение без ссылки) и `url` (чистая ссылка карточки/приглашения).
 * Компонент склеивает `full = message + " " + url` (ПРОБЕЛ, не `\n` — перенос
 * строки давал дубль ссылки в Telegram) и отдаёт `full` во все 3 канала: WhatsApp/MAX
 * — в `?text=`, Telegram — в `url=` (на проде показывает сообщение, затем ссылку
 * инлайн один раз + выбор контакта). Раздельные `message`/`url` сохранены под будущие
 * короткие ссылки. Системный `navigator.share` не используем (показывал чужие
 * контакты и не имел MAX).
 *
 * Заголовок sheet настраивается опциональным `title` (default «Переслать
 * клиенту» — карточки товаров; реферальный экран Блока 11 шлёт «Пригласить
 * друга»). Call-site без `title` сохраняет прежнее поведение.
 *
 * MAX на Android — app-схема (`max://`): её НЕЛЬЗЯ открывать в новой вкладке
 * (`target=_blank` → Chrome грузит сайт), нужна навигация в top-frame → для
 * app-схем убираем target/rel (anchor-клик = location.href). http(s)-каналы
 * (WhatsApp/Telegram/MAX-desktop) — как раньше, в новой вкладке.
 *
 * Design-lock (P-038/039/019): bg-card, border-accent, text-ink, min-h-tap,
 * вертикальный flex-col gap-3 — без визуального/DOM-регресса.
 */

const FORWARD_TITLE = 'Переслать клиенту';

const FORWARD_ITEM_CLASS = [
  'w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink',
  'text-[16px] py-3 px-4 flex items-center gap-3 no-underline',
  'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
].join(' ');

export interface ForwardSheetProps {
  open: boolean;
  onClose: () => void;
  /** Сообщение пересылки БЕЗ ссылки (call-site формирует под свой блок). */
  message: string;
  /** Чистая ссылка карточки/приглашения. Telegram кладёт её в `url=` (превью-
   *  карточка); WhatsApp/MAX/копирование — `message + " " + url` инлайн. */
  url: string;
  /** «Скопировать сообщение» — clipboard в Container (получает `message + " " + url`). */
  onCopyMessage: (text: string) => void;
  /**
   * Заголовок sheet. Default `'Переслать клиенту'` (карточки декора/фурнитуры —
   * пересылка товара клиенту). Реферальный экран (Блок 11) передаёт
   * `'Пригласить друга'`. Backward-compatible: call-site без `title` сохраняет
   * прежний заголовок.
   */
  title?: string;
}

export function ForwardSheet({
  open,
  onClose,
  message,
  url,
  onCopyMessage,
  title = FORWARD_TITLE,
}: ForwardSheetProps) {
  // Единый текст «сообщение + ссылка», склейка через ПРОБЕЛ (не `\n` — перенос
  // строки давал дубль ссылки в Telegram-рефералке). Все 3 канала берут `full`:
  // Telegram кладёт его в `url=` (на проде показывает сообщение, затем ссылку
  // инлайн один раз + выбор контакта); WhatsApp/MAX — в свои `?text=`.
  const full = `${message} ${url}`;
  const items = [
    { key: 'wa', label: 'WhatsApp', href: buildWhatsAppShareLink(full) },
    { key: 'tg', label: 'Telegram', href: buildTelegramShareLink(full) },
    { key: 'max', label: 'MAX', href: buildMaxShareLink(full) },
  ];
  return (
    <BottomSheet isOpen={open} title={title} onClose={onClose} maxHeightVh={60}>
      <div className="flex flex-col gap-3">
        {items.map((it) => {
          const isAppScheme = !/^https?:/i.test(it.href);
          return (
            <a
              key={it.key}
              href={it.href}
              {...(isAppScheme ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
              aria-label={`Переслать через ${it.label}`}
              onClick={onClose}
              className={FORWARD_ITEM_CLASS}
            >
              <span className="flex-1 text-left">{it.label}</span>
              <span aria-hidden="true" className="text-muted">
                ↗
              </span>
            </a>
          );
        })}
        <button
          type="button"
          aria-label="Скопировать сообщение"
          onClick={() => {
            onCopyMessage(full);
            onClose();
          }}
          className={FORWARD_ITEM_CLASS}
        >
          <span className="flex-1 text-left">Скопировать сообщение</span>
        </button>
      </div>
    </BottomSheet>
  );
}
